import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid2,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  Container,
  TextField,
  MenuItem,
  debounce,
  Button,
  keyframes,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Collapse,
  Skeleton,
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  DragIndicator as DragIndicatorIcon,
  ContentCopy as ContentCopyIcon,
  OpenInNew as OpenInNewIcon,
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
  Info as InfoIcon,
  Timeline as TimelineIcon,
  TrendingFlat as TrendingFlatIcon,
  AttachMoney as AttachMoneyIcon,
  SwapHoriz as SwapHorizIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import Chart from "./Chart";
import { useSearchParams } from "react-router-dom";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { formatNumberHumanize } from "../utils/number";
import { useTrades } from "../hooks/useTrades";
import { usePair } from "../hooks/usePair";
import Avatar from "./Avatar";
import { calculateTokenAge, formatDuration } from "../utils/token.js";

const ChartPage = () => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const [pairAddress, setPairAddress] = useState(
    searchParams.get("pair") || "0x88e6a0c2ddd26feeb64f039a2c41296fcb3f5640"
  );
  const [chartHeight, setChartHeight] = useState(60);
  const [isDragging, setIsDragging] = useState(false);
  const [dragHandlePosition, setDragHandlePosition] = useState(0);
  const dragStartRef = useRef({ y: 0, height: 0 });
  const currentMouseRef = useRef({ y: 0 });
  const chartContainerRef = useRef(null);
  const [filters, setFilters] = useState({
    type: undefined,
    minAmount: undefined,
    maxAmount: undefined,
  });
  const tableBodyRef = useRef(null);
  const [minAmountInput, setMinAmountInput] = useState("");
  const [maxAmountInput, setMaxAmountInput] = useState("");

  // State management
  const [state, setState] = useState({
    // Price state
    prices: {
      current: null,
      previous: null,
      native: null,
      change: 0,
      animation: null,
    },
    // Trade state
    trades: {
      list: [],
      loading: false,
      hasMore: true,
      total: 0,
    },
    // Pair state
    pair: {
      data: null,
      loading: false,
      error: null,
    },
  });

  const { pair: pairData, loading: pairLoading } = usePair(1, pairAddress);
  const {
    trades,
    loading,
    isLoadingMore,
    hasMore,
    loadMore,
    totalTrades,
    onTrade,
  } = useTrades(pairAddress, filters, pairLoading);

  const flashAnimation = keyframes`
    0% { background-color: transparent; }
    50% { background-color: ${theme.palette.success.main}15; }
    100% { background-color: transparent; }
  `;

  const flashDownAnimation = keyframes`
    0% { background-color: transparent; }
    50% { background-color: ${theme.palette.error.main}15; }
    100% { background-color: transparent; }
  `;

  useEffect(() => {
    const handleScroll = (e) => {
      const tableBody = e.target;
      const { scrollTop, scrollHeight, clientHeight } = tableBody;
      const percentDownFromTop =
        (scrollTop / (scrollHeight - clientHeight)) * 100;
      if (percentDownFromTop > 95) {
        loadMore();
      }
    };
    tableBodyRef.current?.addEventListener("scroll", handleScroll);
    return () =>
      tableBodyRef.current?.removeEventListener("scroll", handleScroll);
  }, [loading, isLoadingMore, hasMore]);

  useEffect(() => {
    const newPairAddress = searchParams.get("pair");
    if (newPairAddress && newPairAddress !== pairAddress) {
      // Clear trades state before updating pair address
      setState((prev) => ({
        ...prev,
        trades: {
          list: [],
          loading: true,
          hasMore: true,
          total: 0,
        },
        prices: {
          current: null,
          previous: null,
          native: null,
          change: 0,
          animation: null,
        },
      }));
      setPairAddress(newPairAddress);
    }
  }, [searchParams]);

  const handleDragStart = (e) => {
    e.preventDefault();
    setIsDragging(true);
    const clientY = e.type === "touchstart" ? e.touches[0].clientY : e.clientY;
    dragStartRef.current = {
      y: clientY,
      height: chartHeight,
    };
  };

  const handleDragMove = useCallback(
    (e) => {
      if (!isDragging) return;

      const clientY = e.type === "touchmove" ? e.touches[0].clientY : e.clientY;
      currentMouseRef.current.y = clientY;
      const deltaY = clientY - dragStartRef.current.y;
      setDragHandlePosition(deltaY);
    },
    [isDragging]
  );

  const handleDragEnd = useCallback(() => {
    if (!isDragging) return;

    const deltaY = currentMouseRef.current.y - dragStartRef.current.y;
    const deltaHeight = (deltaY / window.innerHeight) * 100;
    const newHeight = Math.max(
      30,
      Math.min(90, dragStartRef.current.height + deltaHeight)
    );

    setChartHeight(newHeight);
    setDragHandlePosition(0);
    setIsDragging(false);

    // Force a reflow to ensure the chart updates
    if (chartContainerRef.current) {
      chartContainerRef.current.style.height =
        chartContainerRef.current.offsetHeight + "px";
      chartContainerRef.current.style.height = `${newHeight}vh`;
    }

    // Trigger resize event
    window.dispatchEvent(new Event("resize"));
  }, [isDragging]);

  // Add effect to handle drag events
  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (e) => {
        e.preventDefault();
        handleDragMove(e);
      };

      const handleMouseUp = (e) => {
        e.preventDefault();
        handleDragEnd();
      };

      const handleTouchMove = (e) => {
        e.preventDefault();
        handleDragMove(e);
      };

      const handleTouchEnd = (e) => {
        e.preventDefault();
        handleDragEnd();
      };

      window.addEventListener("mousemove", handleMouseMove, { passive: false });
      window.addEventListener("mouseup", handleMouseUp, { passive: false });
      window.addEventListener("touchmove", handleTouchMove, { passive: false });
      window.addEventListener("touchend", handleTouchEnd, { passive: false });

      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
        window.removeEventListener("touchmove", handleTouchMove);
        window.removeEventListener("touchend", handleTouchEnd);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);

  const debouncedSetFilters = useCallback(
    debounce((newFilters) => {
      setFilters(newFilters);
    }, 500),
    []
  );

  const handleMinAmountChange = (event) => {
    const value = event.target.value;
    setMinAmountInput(value);
    debouncedSetFilters({ ...filters, minAmount: value });
  };

  const handleMaxAmountChange = (event) => {
    const value = event.target.value;
    setMaxAmountInput(value);
    debouncedSetFilters({ ...filters, maxAmount: value });
  };

  // Set up trade callback for price updates
  useEffect(() => {
    onTrade((newTrade) => {
      setState((prev) => ({
        ...prev,
        prices: {
          current: newTrade.price,
          previous: prev.prices.current,
          native: newTrade.amountNative / newTrade.amountToken,
          change: prev.prices.current
            ? ((newTrade.price - prev.prices.current) / prev.prices.current) *
              100
            : 0,
          animation: prev.prices.current
            ? newTrade.price > prev.prices.current
              ? "up"
              : "down"
            : null,
        },
      }));
    });
  }, [onTrade]);

  // Update pair data
  useEffect(() => {
    if (pairAddress) {
      // Clear all state when pair changes
      setState((prev) => ({
        ...prev,
        pair: {
          data: null,
          loading: true,
          error: null,
        },
        prices: {
          current: null,
          previous: null,
          native: null,
          change: 0,
          animation: null,
        },
        trades: {
          list: [],
          loading: true,
          hasMore: true,
          total: 0,
        },
      }));
    }
  }, [pairAddress]);

  // Update pair data and set initial price
  useEffect(() => {
    if (pairData) {
      setState((prev) => ({
        ...prev,
        pair: {
          data: pairData,
          loading: false,
          error: null,
        },
        prices: {
          current: pairData.priceUSD,
          previous: null,
          native: pairData.priceNative,
          change: 0,
          animation: null,
        },
      }));
    }
  }, [pairData]);

  // Update trades list
  useEffect(() => {
    if (trades) {
      setState((prev) => ({
        ...prev,
        trades: {
          list: trades,
          loading,
          hasMore,
          total: totalTrades,
        },
      }));
    }
  }, [trades, loading, hasMore, totalTrades]);

  // Calculate 24h price change
  const priceChange24h =
    state.pair.data?.priceUSD24h && state.prices.current
      ? ((state.prices.current - state.pair.data.priceUSD24h) /
          state.pair.data.priceUSD24h) *
        100
      : 0;

  // Debug logging
  useEffect(() => {
    console.log("State:", {
      prices: state.prices,
      trades: {
        count: state.trades.list.length,
        loading: state.trades.loading,
        hasMore: state.trades.hasMore,
        total: state.trades.total,
      },
      pair: {
        address: state.pair.data?.address,
        loading: state.pair.loading,
        error: state.pair.error,
        priceUSD: state.pair.data?.priceUSD,
      },
    });
  }, [state]);

  return (
    <Box
      sx={{ p: 0, height: "100%", display: "flex", flexDirection: "column" }}
    >
      {/* Chart Area with Sidebar */}
      <Grid2 container>
        <Grid2
          sx={{
            width: { md: 350 },
            marginBottom: 2,
            flexGrow: { xs: 1, md: 0 },
          }}
        >
          <Paper
            sx={{
              p: 2,
              marginRight: { xs: 0, md: 2 },
              height: "fit-content",
              borderRadius: 1,
              bgcolor: "background.paper",
              border: `1px solid ${theme.palette.divider}`,
            }}
            elevation={2}
          >
            {/* Address Section */}
            <Box>
              {state.pair.loading ? (
                <>
                  {/* Factory/Pair Section Skeleton */}
                  <Box sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        mb: 1,
                      }}
                    >
                      <Skeleton variant="circular" width={32} height={32} />
                      <Box sx={{ flex: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 0.25,
                          }}
                        >
                          <Skeleton variant="text" width={100} />
                          <Skeleton
                            variant="rectangular"
                            width={100}
                            height={24}
                            sx={{
                              borderRadius: 1,
                              bgcolor:
                                theme.palette.mode === "dark"
                                  ? "rgba(255,255,255,0.05)"
                                  : "rgba(0,0,0,0.02)",
                            }}
                          />
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Skeleton
                            variant="text"
                            width={120}
                            sx={{ fontFamily: "monospace" }}
                          />
                          <Skeleton variant="circular" width={20} height={20} />
                        </Box>
                      </Box>
                    </Box>

                    {/* Token Sections */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                      }}
                    >
                      {/* Token */}
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                      >
                        <Skeleton variant="circular" width={24} height={24} />
                        <Box sx={{ flex: 1 }}>
                          <Skeleton
                            variant="text"
                            width={120}
                            sx={{ mb: 0.25 }}
                          />
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Skeleton
                              variant="text"
                              width={100}
                              sx={{ fontFamily: "monospace" }}
                            />
                            <Skeleton
                              variant="circular"
                              width={20}
                              height={20}
                            />
                          </Box>
                        </Box>
                      </Box>

                      {/* Base Token */}
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                      >
                        <Skeleton variant="circular" width={24} height={24} />
                        <Box sx={{ flex: 1 }}>
                          <Skeleton
                            variant="text"
                            width={120}
                            sx={{ mb: 0.25 }}
                          />
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Skeleton
                              variant="text"
                              width={100}
                              sx={{ fontFamily: "monospace" }}
                            />
                            <Skeleton
                              variant="circular"
                              width={20}
                              height={20}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2.5, width: "100%" }} />

                  {/* Market Data Grid */}
                  <Box sx={{ width: "100%" }}>
                    {/* Market Stats */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gap: 1.5,
                        mb: 1.5,
                        "& > div": {
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          p: 1,
                          borderRadius: 1,
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.05)"
                              : "rgba(0,0,0,0.02)",
                        },
                      }}
                    >
                      {["M. Cap", "24h Vol.", "Liquidity"].map((label) => (
                        <Box key={label}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 0.5, fontWeight: 500 }}
                          >
                            {label}
                          </Typography>
                          <Skeleton
                            variant="text"
                            width={60}
                            sx={{ mx: "auto" }}
                          />
                        </Box>
                      ))}
                    </Box>

                    {/* Token Stats */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gap: 1.5,
                        mb: 1.5,
                        "& > div": {
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          p: 1,
                          borderRadius: 1,
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.05)"
                              : "rgba(0,0,0,0.02)",
                        },
                      }}
                    >
                      {["Pool Age", "Token Age", "Supply"].map((label) => (
                        <Box key={label}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 0.5, fontWeight: 500 }}
                          >
                            {label}
                          </Typography>
                          <Skeleton
                            variant="text"
                            width={60}
                            sx={{ mx: "auto" }}
                          />
                        </Box>
                      ))}
                    </Box>

                    {/* Tax Info */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, 1fr)",
                        gap: 1.5,
                        "& > div": {
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          p: 1,
                          borderRadius: 1,
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.05)"
                              : "rgba(0,0,0,0.02)",
                        },
                      }}
                    >
                      {["Buy Tax", "Sell Tax"].map((label) => (
                        <Box key={label}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 0.5, fontWeight: 500 }}
                          >
                            {label}
                          </Typography>
                          <Skeleton
                            variant="text"
                            width={40}
                            sx={{ mx: "auto" }}
                          />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </>
              ) : (
                <>
                  {/* Pair Section */}
                  <Box sx={{ mb: 2 }}>
                    {/* Factory Info */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        mb: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          overflow: "hidden",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "background.paper",
                          border: "1px solid",
                          borderColor: "divider",
                          flexShrink: 0,
                          mt: 0.5,
                        }}
                      >
                        {state.pair.data?.pairFactory?.logoURL ? (
                          <img
                            src={state.pair.data.pairFactory.logoURL}
                            alt={state.pair.data.pairFactory.name}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.6rem",
                              fontWeight: 500,
                              color: "text.secondary",
                            }}
                          >
                            {state.pair.data?.pairFactory?.name?.charAt(0) ||
                              "P"}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 0.25,
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {state.pair.data?.pairFactory?.name ||
                              "Unknown Factory"}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              bgcolor: "background.paper",
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                              border: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            {state.pair.data?.token?.symbol || "Token"} /{" "}
                            {state.pair.data?.baseToken?.symbol || "Base"}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            variant="caption"
                            component="a"
                            href={`https://etherscan.io/address/${state.pair.data?.address}`}
                            target="_blank"
                            sx={{
                              fontFamily: "monospace",
                              color: "text.secondary",
                              textDecoration: "none",
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            {state.pair.data?.address
                              ? `${state.pair.data.address.slice(0, 6)}...${state.pair.data.address.slice(-4)}`
                              : "-"}
                          </Typography>
                          <CopyToClipboard
                            text={state.pair.data?.address || ""}
                          >
                            <IconButton size="small" sx={{ p: 0.25 }}>
                              <ContentCopyIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </CopyToClipboard>
                        </Box>
                      </Box>
                    </Box>

                    {/* Token Info */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                      }}
                    >
                      {/* Token */}
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                      >
                        <Avatar
                          token={state.pair.data?.token}
                          size={24}
                          fontSize={4}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 500, mb: 0.25 }}
                          >
                            {state.pair.data?.token?.name || "Token"}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Typography
                              variant="caption"
                              component="a"
                              href={`https://etherscan.io/address/${state.pair.data?.token?.address}`}
                              target="_blank"
                              sx={{
                                fontFamily: "monospace",
                                color: "text.secondary",
                                textDecoration: "none",
                                "&:hover": { textDecoration: "underline" },
                              }}
                            >
                              {state.pair.data?.token?.address
                                ? `${state.pair.data.token.address.slice(0, 6)}...${state.pair.data.token.address.slice(-4)}`
                                : "-"}
                            </Typography>
                            <CopyToClipboard
                              text={state.pair.data?.token?.address || ""}
                            >
                              <IconButton size="small" sx={{ p: 0.25 }}>
                                <ContentCopyIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </CopyToClipboard>
                          </Box>
                        </Box>
                      </Box>

                      {/* Base Token */}
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                      >
                        <Avatar
                          token={state.pair.data?.baseToken}
                          size={24}
                          fontSize={4}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 500, mb: 0.25 }}
                          >
                            {state.pair.data?.baseToken?.name || "Base Token"}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Typography
                              variant="caption"
                              component="a"
                              href={`https://etherscan.io/address/${state.pair.data?.baseToken?.address}`}
                              target="_blank"
                              sx={{
                                fontFamily: "monospace",
                                color: "text.secondary",
                                textDecoration: "none",
                                "&:hover": { textDecoration: "underline" },
                              }}
                            >
                              {state.pair.data?.baseToken?.address
                                ? `${state.pair.data.baseToken.address.slice(0, 6)}...${state.pair.data.baseToken.address.slice(-4)}`
                                : "-"}
                            </Typography>
                            <CopyToClipboard
                              text={state.pair.data?.baseToken?.address || ""}
                            >
                              <IconButton size="small" sx={{ p: 0.25 }}>
                                <ContentCopyIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </CopyToClipboard>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  <Divider
                    sx={{
                      my: 2.5,
                      width: "100%",
                      // borderColor:
                      //   theme.palette.mode === "dark"
                      //     ? "rgba(255, 255, 255, 0.12)"
                      //     : "rgba(0, 0, 0, 0.12)",
                      opacity: 1,
                    }}
                  />

                  {/* Market Data Grid */}
                  <Box sx={{ width: "100%" }}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                        gap: 1.5,
                        mb: 1.5,
                        "& > div": {
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          p: 0.75,
                          borderRadius: 1,
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.05)"
                              : "rgba(0,0,0,0.02)",
                          minWidth: 0,
                        },
                      }}
                    >
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 0.25, fontWeight: 500 }}
                        >
                          M. Cap
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "monospace",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            width: "100%",
                            textAlign: "center",
                          }}
                        >
                          $
                          {formatNumberHumanize(
                            state.prices.current *
                              state.pair.data?.token.supply || 0,
                            {
                              decimals: 2,
                            }
                          )}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 0.25, fontWeight: 500 }}
                        >
                          24h Vol.
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "monospace",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            width: "100%",
                            textAlign: "center",
                          }}
                        >
                          $
                          {formatNumberHumanize(
                            state.pair.data?.volume24h || 0,
                            {
                              decimals: 2,
                            }
                          )}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 0.25, fontWeight: 500 }}
                        >
                          Liquidity
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "monospace",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            width: "100%",
                            textAlign: "center",
                          }}
                        >
                          $
                          {formatNumberHumanize(
                            state.pair.data?.liquidity || 0,
                            {
                              decimals: 2,
                            }
                          )}
                        </Typography>
                      </Box>
                    </Box>

                    {/* New info boxes for pool age, token age, and supply */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gap: 1.5,
                        mb: 1.5,
                        "& > div": {
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          p: 0.75,
                          borderRadius: 1,
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.05)"
                              : "rgba(0,0,0,0.02)",
                        },
                      }}
                    >
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 0.25, fontWeight: 500 }}
                        >
                          Pool Age
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "monospace",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            width: "100%",
                            textAlign: "center",
                          }}
                        >
                          {state.pair.data?.createdAt
                            ? formatDuration(state.pair.data.createdAt)
                            : "N/A"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 0.25, fontWeight: 500 }}
                        >
                          Token Age
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "monospace",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            width: "100%",
                            textAlign: "center",
                          }}
                        >
                          {state.pair.data?.token
                            ? calculateTokenAge(state.pair.data.token)
                            : "N/A"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 0.25, fontWeight: 500 }}
                        >
                          Supply
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "monospace",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            width: "100%",
                            textAlign: "center",
                          }}
                        >
                          {formatNumberHumanize(
                            state.pair.data?.token?.supply || 0,
                            {
                              decimals: 2,
                            }
                          )}
                        </Typography>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, 1fr)",
                        gap: 1.5,
                        "& > div": {
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          p: 0.75,
                          borderRadius: 1,
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.05)"
                              : "rgba(0,0,0,0.02)",
                        },
                      }}
                    >
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 0.25, fontWeight: 500 }}
                        >
                          Buy Tax
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "monospace",
                            fontWeight: 600,
                            color: "success.main",
                          }}
                        >
                          {state.pair.data?.buyTax ?? "?"}%
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 0.25, fontWeight: 500 }}
                        >
                          Sell Tax
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "monospace",
                            fontWeight: 600,
                            color: "error.main",
                          }}
                        >
                          {state.pair.data?.sellTax ?? "?"}%
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          </Paper>
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 12, md: "grow" }}>
          {/* Pair Info Header */}
          <Paper
            sx={{
              p: 2,
              borderRadius: 1,
              borderBottomRightRadius: 0,
              borderBottomLeftRadius: 0,
              bgcolor: "background.paper",
              border: `1px solid ${theme.palette.divider}`,
            }}
            elevation={2}
          >
            {state.pair.loading ? (
              <Box sx={{ width: "100%" }}>
                <Grid2 container spacing={2} alignItems="center">
                  <Grid2 size="auto">
                    <Skeleton variant="circular" width={40} height={40} />
                  </Grid2>
                  <Grid2 size="auto">
                    <Skeleton variant="text" width={200} height={32} />
                    <Skeleton variant="text" width={120} height={24} />
                  </Grid2>
                  <Grid2 size="auto" sx={{ ml: "auto" }}>
                    <Skeleton variant="text" width={160} height={40} />
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        justifyContent: "flex-end",
                      }}
                    >
                      <Skeleton variant="text" width={80} height={24} />
                      <Skeleton variant="text" width={60} height={24} />
                    </Box>
                  </Grid2>
                </Grid2>
              </Box>
            ) : (
              <>
                {state.pair.data?.token?.scamAt && (
                  <Box
                    sx={{
                      width: "100%",
                      p: 2.5,
                      mb: 2.5,
                      borderRadius: 1,
                      background:
                        theme.palette.mode === "dark"
                          ? `linear-gradient(135deg, ${theme.palette.error.dark}80, ${theme.palette.error.main}40)`
                          : `linear-gradient(135deg, ${theme.palette.error.light}30, ${theme.palette.error.main}15)`,
                      backdropFilter: "blur(10px)",
                      border: `1px solid ${theme.palette.error[theme.palette.mode === "dark" ? "dark" : "light"]}40`,
                      color:
                        theme.palette.error[
                          theme.palette.mode === "dark" ? "light" : "main"
                        ],
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 3,
                      position: "relative",
                      overflow: "hidden",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "2px",
                        background: `linear-gradient(90deg, ${theme.palette.error.main}, ${theme.palette.error.light}50)`,
                      },
                    }}
                  >
                    <WarningIcon
                      sx={{
                        fontSize: 32,
                        mt: 0.5,
                        color:
                          theme.palette.error[
                            theme.palette.mode === "dark" ? "light" : "main"
                          ],
                        opacity: 0.9,
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          letterSpacing: "0.5px",
                          mb: 0.5,
                          color: "inherit",
                        }}
                      >
                        Security Alert
                      </Typography>
                      <Typography
                        sx={{
                          opacity: 0.9,
                          lineHeight: 1.5,
                          color:
                            theme.palette.mode === "dark"
                              ? theme.palette.error.light
                              : theme.palette.error.dark,
                          fontWeight: 500,
                        }}
                      >
                        This token has been identified as potentially unsafe.
                        Please exercise extreme caution and thoroughly verify
                        before trading.
                      </Typography>
                    </Box>
                  </Box>
                )}
                <Grid2 container spacing={2} alignItems="center">
                  <Grid2 size="auto">
                    <Avatar token={state.pair.data?.token} />
                  </Grid2>
                  <Grid2 size="auto">
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 600,
                          letterSpacing: "-0.5px",
                        }}
                      >
                        {state.pair.data?.token?.name || "Token"}
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          color: "text.secondary",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        {state.pair.data?.token?.symbol || "Token"} /{" "}
                        {state.pair.data?.baseToken?.symbol || "Base"}
                      </Typography>
                    </Box>
                  </Grid2>
                  <Grid2 size="auto" sx={{ ml: "auto" }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: { xs: "center", md: "flex-end" },
                        gap: { xs: 2, md: 2 },
                        p: { xs: 0, md: 0 },
                        width: "100%",
                        borderRadius: 1,
                        animation:
                          state.prices.animation === "up"
                            ? `${flashAnimation} 0.5s ease-in-out`
                            : state.prices.animation === "down"
                              ? `${flashDownAnimation} 0.5s ease-in-out`
                              : "none",
                      }}
                    >
                      <Typography
                        variant="h4"
                        sx={{
                          fontFamily: "monospace",
                          fontWeight: 700,
                          letterSpacing: "-0.5px",
                          color: "text.primary",
                          fontSize: { xs: "1.75rem", md: "2rem" },
                        }}
                      >
                        ${formatNumberHumanize(state.prices.current || 0)}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-end",
                          gap: 0.25,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color:
                              priceChange24h >= 0
                                ? "success.main"
                                : "error.main",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            fontWeight: 600,
                            fontSize: { xs: "0.875rem", md: "0.875rem" },
                            fontFamily: "monospace",
                          }}
                        >
                          {priceChange24h >= 0 ? (
                            <TrendingUpIcon color="success" />
                          ) : (
                            <TrendingDownIcon color="error" />
                          )}
                          {`${priceChange24h >= 0 ? "+" : ""}${formatNumberHumanize(priceChange24h, { decimals: 1 })}%`}
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              ml: 0.5,
                              fontSize: { xs: "0.75rem", md: "0.75rem" },
                            }}
                          >
                            24h
                          </Typography>
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            fontSize: { xs: "0.875rem", md: "0.875rem" },
                            fontFamily: "monospace",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          {formatNumberHumanize(state.prices.native || 0)} ETH
                        </Typography>
                      </Box>
                    </Box>
                  </Grid2>
                </Grid2>
              </>
            )}
          </Paper>
          <Box sx={{ display: "flex", flex: 1, gap: 2, borderRadius: 1 }}>
            {/* Main Chart Area */}
            <Paper
              ref={chartContainerRef}
              sx={{
                flex: 1,
                p: 0,
                mb: 0,
                borderRadius: 1,
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                bgcolor: "background.paper",
                border: `1px solid ${theme.palette.divider}`,
                height: `${chartHeight}vh`,
                display: "flex",
                flexDirection: "column",
                background: theme.palette.background.default,
                position: "relative",
              }}
            >
              {!state.pair.data ? (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <Skeleton variant="rectangular" width="90%" height="70%" />
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      width: "90%",
                    }}
                  >
                    <Skeleton variant="rectangular" width="33%" height={24} />
                    <Skeleton variant="rectangular" width="33%" height={24} />
                    <Skeleton variant="rectangular" width="33%" height={24} />
                  </Box>
                </Box>
              ) : (
                <Chart
                  pairAddress={state.pair.data?.address}
                  price={state.pair.data?.priceUSD}
                  isUSD={true} // TODO: for later when not only USD
                />
              )}
              {isDragging && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 9998,
                    backgroundColor: "transparent",
                    cursor: "ns-resize",
                    touchAction: "none",
                  }}
                />
              )}
              <Box
                onMouseDown={handleDragStart}
                onTouchStart={handleDragStart}
                sx={{
                  position: "relative",
                  height: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "ns-resize",
                  borderRadius: 9,
                  borderTopRightRadius: 0,
                  borderTopLeftRadius: 0,
                  backgroundColor: theme.palette.action.hover,
                  borderTop: `1px solid ${theme.palette.divider}`,
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                  transition: isDragging ? "none" : "all 0.1s ease-in-out",
                  transform: isDragging
                    ? `translateY(${dragHandlePosition}px)`
                    : "none",
                  zIndex: 2,
                  userSelect: "none",
                  WebkitUserSelect: "none",
                  MozUserSelect: "none",
                  msUserSelect: "none",
                  pointerEvents: "auto",
                  touchAction: "none",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: "-10px",
                    left: 0,
                    right: 0,
                    height: "20px",
                    backgroundColor: "transparent",
                    zIndex: -1,
                  },
                }}
              >
                <DragIndicatorIcon
                  sx={{
                    color: theme.palette.text.secondary,
                    transform: "rotate(90deg)",
                    fontSize: "20px",
                  }}
                />
              </Box>
            </Paper>
          </Box>
          {/* Trades List */}
          <Paper
            sx={{
              marginTop: 2,
              marginBottom: 2,
              flex: 1,
              display: "flex",
              flexDirection: "column",
              borderRadius: 1,
              bgcolor: "background.paper",
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box sx={{ p: 1, borderBottom: 1, borderColor: "divider" }}>
              <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 4, md: 3 }}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Trade Type"
                    value={filters.type || ""}
                    onChange={(e) =>
                      setFilters({ ...filters, type: e.target.value || null })
                    }
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="BUY">Buy</MenuItem>
                    <MenuItem value="SELL">Sell</MenuItem>
                  </TextField>
                </Grid2>
                <Grid2 size={{ xs: 4, md: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Min Amount (USD)"
                    value={minAmountInput}
                    onChange={handleMinAmountChange}
                  />
                </Grid2>
                <Grid2 size={{ xs: 4, md: 3 }}>
                  <TextField
                    fullWidth
                    type="number"
                    size="small"
                    label="Max Amount (USD)"
                    value={maxAmountInput}
                    onChange={handleMaxAmountChange}
                  />
                </Grid2>
              </Grid2>
            </Box>
            <Box
              sx={{
                flex: 1,
                position: "relative",
                overflow: "auto",
                height: "100%",
                minHeight: "calc(100vh - 170px)",
                "&::-webkit-scrollbar": {
                  width: "8px",
                  height: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: theme.palette.background.default,
                },
                "&::-webkit-scrollbar-thumb": {
                  background: theme.palette.divider,
                  borderRadius: "4px",
                  "&:hover": {
                    background: theme.palette.text.secondary,
                  },
                },
              }}
            >
              <TableContainer
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  overflow: "auto",
                }}
                ref={tableBodyRef}
              >
                <Table
                  size="small"
                  sx={{ tableLayout: "fixed", width: "100%", minWidth: 800 }}
                  stickyHeader
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: "14%" }}>Date</TableCell>
                      <TableCell align="right" sx={{ width: "10%" }}>
                        Price
                      </TableCell>
                      <TableCell align="right" sx={{ width: "10%" }}>
                        Total
                      </TableCell>
                      <TableCell align="right" sx={{ width: "10%" }}>
                        {state.pair.data?.token?.symbol || "Token"}
                      </TableCell>
                      <TableCell align="right" sx={{ width: "10%" }}>
                        {state.pair.data?.baseToken?.symbol || "Base"}
                      </TableCell>
                      <TableCell align="right" sx={{ width: "10%" }}>
                        Total ETH
                      </TableCell>
                      <TableCell sx={{ width: "13%" }}>Maker</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(loading || state.pair.loading) &&
                    state.trades.list.length === 0
                      ? // Initial loading state with skeletons that fill the container
                        Array.from({
                          length: Math.max(
                            20,
                            Math.floor((window.innerHeight - 300) / 40)
                          ),
                        }).map((_, index) => (
                          <TableRow key={`skeleton-${index}`}>
                            <TableCell>
                              <Skeleton variant="text" width="80%" />
                            </TableCell>
                            <TableCell align="right">
                              <Skeleton
                                variant="text"
                                width="60%"
                                sx={{ ml: "auto" }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Skeleton
                                variant="text"
                                width="70%"
                                sx={{ ml: "auto" }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Skeleton
                                variant="text"
                                width="65%"
                                sx={{ ml: "auto" }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Skeleton
                                variant="text"
                                width="65%"
                                sx={{ ml: "auto" }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Skeleton
                                variant="text"
                                width="50%"
                                sx={{ ml: "auto" }}
                              />
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Skeleton variant="text" width="60%" />
                                <Skeleton
                                  variant="circular"
                                  width={20}
                                  height={20}
                                />
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      : state.trades.list.map((trade, index) => {
                          const value = trade.amountUSD;
                          const maxValue = Math.max(
                            ...state.trades.list.map((t) => t.amountUSD)
                          );
                          const opacity = Math.min(value / maxValue, 1) * 0.8;

                          return (
                            <TableRow
                              key={`${trade.transactionHash}-${trade.timestamp}-${index}`}
                              sx={{
                                cursor: "pointer",
                                transition: "all 0.3s ease-in-out",
                                background:
                                  trade.type === "BUY"
                                    ? `linear-gradient(to right, rgba(76, 175, 80, 0.3) ${opacity * 100}vw, rgba(76, 175, 80, 0.1) ${opacity * 100}vw)`
                                    : `linear-gradient(to right, rgba(244, 67, 54, 0.3) ${opacity * 100}vw, rgba(244, 67, 54, 0.1) ${opacity * 100}vw)`,
                                "&:hover": {
                                  background:
                                    trade.type === "BUY"
                                      ? `linear-gradient(to right, rgba(76, 175, 80, 0.4) ${opacity * 100}vw, rgba(76, 175, 80, 0.15) ${opacity * 100}vw)`
                                      : `linear-gradient(to right, rgba(244, 67, 54, 0.4) ${opacity * 100}vw, rgba(244, 67, 54, 0.15) ${opacity * 100}vw)`,
                                },
                              }}
                            >
                              <TableCell>
                                <Box
                                  sx={{
                                    position: "relative",
                                    width: "100%",
                                    height: "100%",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      top: 0,
                                      left: 0,
                                      width: "100%",
                                      height: "100%",
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{ position: "relative", zIndex: 1 }}
                                  >
                                    {new Date(trade.timestamp).toLocaleString(
                                      "en-US",
                                      {
                                        month: "short",
                                        day: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        second: "2-digit",
                                        hour12: false,
                                      }
                                    )}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2">
                                  ${formatNumberHumanize(trade.price)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2">
                                  ${formatNumberHumanize(trade.amountUSD)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2">
                                  {formatNumberHumanize(trade.amountToken)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2">
                                  {formatNumberHumanize(trade.amountBase)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2">
                                  Ξ{formatNumberHumanize(trade.amountNative)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    component="a"
                                    href={`https://etherscan.io/tx/${trade.transactionHash}`}
                                    target="_blank"
                                    sx={{
                                      textDecoration: "none",
                                      color: "inherit",
                                      "&:hover": {
                                        textDecoration: "underline",
                                      },
                                    }}
                                  >
                                    {`${trade.maker.slice(0, 6)}...${trade.maker.slice(-4)}`}
                                  </Typography>
                                  <CopyToClipboard text={trade.maker}>
                                    <IconButton
                                      size="small"
                                      sx={{ padding: 0 }}
                                    >
                                      <ContentCopyIcon
                                        fontSize="small"
                                        sx={{ fontSize: 14 }}
                                      />
                                    </IconButton>
                                  </CopyToClipboard>
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    {/* Loading more indicator */}
                    {isLoadingMore && (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              py: 2,
                              gap: 2,
                            }}
                          >
                            <Box
                              sx={{
                                width: 4,
                                height: 4,
                                borderRadius: "50%",
                                bgcolor: "primary.main",
                                animation: `${keyframes`
                                0%, 100% { opacity: 0.2; }
                                50% { opacity: 1; }
                              `} 1s ease-in-out infinite`,
                                animationDelay: "0s",
                              }}
                            />
                            <Box
                              sx={{
                                width: 4,
                                height: 4,
                                borderRadius: "50%",
                                bgcolor: "primary.main",
                                animation: `${keyframes`
                                0%, 100% { opacity: 0.2; }
                                50% { opacity: 1; }
                              `} 1s ease-in-out infinite`,
                                animationDelay: "0.33s",
                              }}
                            />
                            <Box
                              sx={{
                                width: 4,
                                height: 4,
                                borderRadius: "50%",
                                bgcolor: "primary.main",
                                animation: `${keyframes`
                                0%, 100% { opacity: 0.2; }
                                50% { opacity: 1; }
                              `} 1s ease-in-out infinite`,
                                animationDelay: "0.66s",
                              }}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {!hasMore && state.trades.list.length > 0 && (
                <Box sx={{ p: 2, textAlign: "center" }}>
                  <Typography>No more trades to load</Typography>
                </Box>
              )}
            </Box>
            {/* Table Footer */}
            <Box
              sx={{
                bgcolor: theme.palette.background.paper,
                borderTop: `1px solid ${theme.palette.divider}`,
                p: 1,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                borderRadius: 1,
                borderTopRightRadius: 0,
                borderTopLeftRadius: 0,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Total Trades: {state.trades.total.toLocaleString()}
              </Typography>
            </Box>
          </Paper>
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default ChartPage;
